import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Loader2, PlugZap } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError, AuthNotReadyError } from '@/api/client';
import { useMe } from '@/api/queries';
import { useAuth } from '@/auth/AuthProvider';
import { SignInScreen } from '@/auth/SignInScreen';
import { EmptyState } from '@/components/EmptyState';
import { useAskLauncher } from '@/features/ask';
import { documentTitle, useMeta } from '@/app/meta';
import { AppCommandPalette } from './AppCommandPalette';
import { FindAnything } from '@/features/apps/FindAnything';
import { ReleaseStageBanner } from './ReleaseStageBanner';
import { currentTitle, TopBar } from './TopBar';
import { PlatformBar } from './PlatformBar';
import { activeMenuId, AppCrumb, AppMenuBar, resolveMenuBar } from '@/app/app-shell';
import { applyDensity, Button, PageShell, readDensity, useDisclosureValue } from '@/kit';
import { useCoreWowFlag } from '@/features/wow/core-flags';
import { PageEyebrowContext } from './page-eyebrow';
import { pageName, useMainHeading } from './page-title';
import { adminGroups, appForLocation, keepInApp, navigableItems, platformAt, platformOf } from '@/app/platform';
import type { Me } from '@/api/types';
import { ExperienceProvider, useExperience } from '@/app/experience/ExperienceProvider';
import { menuBarAt, menuBarScoped } from '@/app/experience/tiers';
import { ViewAsBanner } from '@/app/view-as/ViewAsBanner';
import { appForPath, useApps } from '@/features/apps/apps-api';
import { FeedbackHost } from '@/features/feedback/FeedbackHost';
import { QuickLinksProvider, QuickLinksRow } from '@/features/quick-links/QuickLinks';

export function AppShell() {
  const { ready, signedIn, credentialAttached, mode, setDevUser, signOut } = useAuth();
  const me = useMe(signedIn);
  const unauthorized = me.error instanceof ApiError && me.error.isAuth;
  const notReadyYet = me.error instanceof AuthNotReadyError;
  const { appName } = useMeta();

  useEffect(() => {
    if (unauthorized && mode === 'dev') setDevUser(null);
  }, [unauthorized, mode, setDevUser]);

  useEffect(() => {
    if (me.error && !(me.error instanceof AuthNotReadyError) && !(me.error instanceof ApiError && (me.error.isAuth || me.error.isMissing))) {
      toast.error('Could not load your profile', { description: me.error.message });
    }
  }, [me.error]);

  // MSAL has not finished deciding who is signed in: showing the sign-in screen here is what
  // made a signed-in user bounce, so wait instead.
  if (!ready) return <ConnectingScreen />;
  if (!signedIn) return <SignInScreen />;

  // A 401 that arrived WITH a credential is a real refusal: the account is not provisioned.
  // A failure without one is a timing problem, and retrying is the right answer.
  if (unauthorized && credentialAttached && mode === 'entra') {
    return (
      <NotProvisionedScreen
        appName={appName}
        message={me.error instanceof Error ? me.error.message : ''}
        onRetry={() => void me.refetch()}
        onSignOut={signOut}
      />
    );
  }
  if (notReadyYet) return <ConnectingScreen />;

  return (
    <ExperienceProvider enabled={Boolean(me.data)}>
      {/* R-15 D2 (T-111): every panel's feedback pill opens one form, held here */}
      <FeedbackHost me={me.data}>
        <ShellFrame me={me.data} loading={me.isLoading} signedIn={signedIn} />
      </FeedbackHost>
    </ExperienceProvider>
  );
}

/**
 * The signed-in shell. R-14 T-087/T-070: every L0 entry, L1 item and shell action passes the user's
 * experience tier (the declaration already carries only what the roles allow); T-088: the View as
 * ribbon sits under the release ribbon while a persona is active.
 */
function ShellFrame({ me, loading, signedIn }: { me: Me | undefined; loading: boolean; signedIn: boolean }) {
  // One launcher for the whole shell: the topbar button and the ⌘K entry open the Ask page (R-16 T-127: a page, not a panel).
  const ask = useAskLauncher();
  const launchAsk = () => ask.launch();
  const { appName, platformName } = useMeta();
  const { pathname, search } = useLocation();
  const apps = useApps(signedIn && Boolean(me));
  const appList = apps.data?.apps ?? [];
  // #614 QA11-P1-10: a screen belongs to the app whose menus link it; a platform page belongs to no app.
  const currentApp = appForLocation(appList, pathname, search, appForPath);
  // R-14 T-070: the app's menus at the user's tier (roles already applied by the API)
  const { tier, minTier } = useDisclosureValue();
  // T-068: `{scopeKey}` in a declared route is the user's chosen scope (GET /api/me/home)
  const { home } = useExperience();
  const menus = currentApp
    ? menuBarScoped(menuBarAt(resolveMenuBar(currentApp), tier, minTier), home?.chosen?.key).map((m) => ({ ...m, items: m.items.map((it) => ({ ...it, route: keepInApp(it.route, currentApp.slug) })) }))
    : [];
  // #649: the L0 entries and Admin groups come from the platform declaration on GET /api/apps.
  const platform = platformAt(platformOf(apps.data), tier, minTier);
  const roles = me?.roles.map((r) => r.role) ?? [];
  const nav = navigableItems(platform, currentApp ? { menuBar: menus } : null, roles);
  const brand = platformName?.trim() || platform.name || appName;
  const pageTitle = currentTitle(pathname, nav, '');
  // #727: the default page eyebrow — "<App> · <menu>" inside an app, the platform's name on a platform page
  const here = menus.find((m) => m.id === activeMenuId(menus, pathname, search) && m.id !== 'app');
  const eyebrow = currentApp ? (here ? `${currentApp.name} · ${here.label}` : currentApp.name) : brand || null;
  // #649: the platform display name (app_setting via /api/meta) on every title; the app's name only inside the app.
  // #779: the page's own title (its h1 in <main>) names the tab; the menu label only while there is none
  const [mainEl, setMainEl] = useState<HTMLElement | null>(null);
  const heading = useMainHeading(mainEl, pathname);
  const tabName = pageName(heading, pageTitle);
  useEffect(() => {
    document.title = documentTitle({ appName: currentApp?.name ?? '', platformName: brand }, tabName || undefined);
  }, [brand, currentApp?.name, tabName]);
  // `--shell-top` (#165): the top bar plus the release-stage banner, measured, so a full-height
  // page (`h-[calc(100vh-var(--shell-top))]`) and the drawer end at the viewport's bottom edge —
  // a page sized to the top bar alone ran 33px past it and took the table's own horizontal
  // scrollbar off screen with it.
  const [column, setColumn] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!column || typeof ResizeObserver === 'undefined') return;
    const main = column.querySelector(':scope > main');
    const update = () => {
      if (!main) return;
      // R-13: the kit content region pads the page top and bottom (PageShell), so a full-height page
      // (h-[calc(100vh-var(--shell-top))]) counts that padding in --shell-top and ends at the window's edge.
      const pad = getComputedStyle(main);
      const top = Math.round(main.getBoundingClientRect().top + window.scrollY + Number.parseFloat(pad.paddingTop) + Number.parseFloat(pad.paddingBottom));
      document.documentElement.style.setProperty('--shell-top', `${top}px`);
    };
    const ro = new ResizeObserver(update);
    const observeBars = () => {
      for (const el of Array.from(column.children)) if (el !== main) ro.observe(el);
    };
    observeBars();
    // A bar mounted after this effect ran (the app bar or breadcrumb once /api/apps answers) must be
    // measured too, or --shell-top stays short by its height on every full-height page.
    const mo = new MutationObserver(() => {
      observeBars();
      update();
    });
    mo.observe(column, { childList: true });
    update();
    return () => {
      ro.disconnect();
      mo.disconnect();
    };
  }, [column]);

  // R-13 wow (wow.density): the reader's density, remembered per user; compact while the flag is off.
  const densityOn = useCoreWowFlag('density');
  const userId = me?.id ?? null;
  useEffect(() => {
    applyDensity(densityOn && userId ? readDensity(userId) : 'compact');
  }, [densityOn, userId]);


  return (
    // R-16 T-135: the reader's quick links for the app they are in (the row under the app bar, the stars in its menus)
    <QuickLinksProvider app={currentApp?.slug ?? null}>
    <PageShell
      columnRef={setColumn}
      mainRef={setMainEl}
      l0={
        <PlatformBar platformName={brand} entries={platform.entries} roles={roles} apps={appList} dataSections={platform.dataSections} adminGroups={adminGroups(platform.adminGroups, roles)}>
          <TopBar me={me} loading={loading} onAsk={launchAsk} nav={nav} title={pageTitle || currentApp?.name || brand} />
        </PlatformBar>
      }
      ribbon={
        <>
          <ReleaseStageBanner />
          <ViewAsBanner />
        </>
      }
      l1={currentApp ? <AppMenuBar app={currentApp} menus={menus} /> : null}
      // v1.2 change 02: ribbon · L0 · L1 · quick links · crumb — the row rides in the crumb slot, before the breadcrumb
      crumb={
        currentApp ? (
          <>
            <QuickLinksRow menus={menus} />
            <AppCrumb app={currentApp} menus={menus} />
          </>
        ) : null
      }
      overlays={
        <>
          <AppCommandPalette me={me} nav={nav} onAsk={launchAsk} />
          <FindAnything />
        </>
      }
    >
      <PageEyebrowContext.Provider value={eyebrow}>
        <Outlet />
      </PageEyebrowContext.Provider>
    </PageShell>
    </QuickLinksProvider>
  );
}

function ConnectingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background" role="status" aria-live="polite">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <span className="text-sm">Signing you in…</span>
      </div>
    </div>
  );
}

function NotProvisionedScreen({
  appName,
  message,
  onRetry,
  onSignOut,
}: {
  appName: string;
  message: string;
  onRetry: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6">
      <EmptyState
        icon={<PlugZap className="h-6 w-6" aria-hidden />}
        title={`Signed in, but ${appName} refused the request`}
        description={
          message
            ? `Your account is signed in, but the API rejected it: ${message}. Ask an administrator to add you as a user.`
            : 'Your account is signed in, but the API rejected it. Ask an administrator to add you as a user.'
        }
        action={{ label: 'Try again', onClick: onRetry }}
      />
      <Button variant="quiet" onClick={onSignOut}>
        Sign in as someone else
      </Button>
    </div>
  );
}
